import { type ReactNode } from 'react';
import { IconQuestionCircle } from '@arco-design/web-react/icon';
import { Tooltip } from '@arco-design/web-react';

interface FieldTipProps {
  /** 说明文案（旧调用兼容；与 content 等价，传其一即可。新代码统一用 content） */
  text?: ReactNode;
  /** 说明内容：支持 ReactNode（富文本/长文案；悬浮面板限宽 320、自动换行，见 components.css） */
  content?: ReactNode;
  /** 追加到问号图标（基础类 .bcc-field-tip-icon）上的类名，如页头场景 .bcc-page-title-tip */
  className?: string;
  /** 可选自定义触发元素（表格单元格/字段值 hover 说明场景，如模板名悬浮看描述）。
   *  传字符串/数字会自动包 <span className="bcc-field-tip-trigger">（help 光标）；
   *  传元素则直接作为触发体（样式自持）。不传则默认问号图标触发（标准形态）。 */
  children?: ReactNode;
}

/**
 * 说明悬浮（frontend-dev-convention §7.3 / §8.5）：
 * label 旁/页头标题右侧问号图标，悬浮展示说明文案——说明文案不塞进 label/subTitle/正文，
 * 全部默认问号悬浮。
 * 用法：<Form.Item label={<span>字段名 <FieldTip content="说明文案" /></span>}>
 *       <PageContainer title="xxx" titleTip="说明文案" />（页头说明统一走 PageContainer titleTip）
 * 图标视觉与悬浮面板排版（icon 次级色/help 光标、content 面板 max-width 320 自动换行）统一在
 * styles/components.css（.bcc-field-tip-icon / .bcc-field-tip-trigger / .bcc-field-tip-content /
 * .bcc-page-title-tip）。children 变体用于「值本身可悬浮看说明」的场景。
 */
export default function FieldTip({ text, content, className, children }: FieldTipProps) {
  const tip = content ?? text;
  const hasTip = tip !== undefined && tip !== null && tip !== '';
  // 无说明文案时：children 变体保持值正常展示（只是没有悬浮），问号形态则整体不渲染
  if (!hasTip) return children !== undefined && children !== null ? <>{children}</> : null;
  const panel = <span className="bcc-field-tip-content">{tip}</span>;
  if (children !== undefined && children !== null) {
    const trigger =
      typeof children === 'string' || typeof children === 'number' ? (
        <span className="bcc-field-tip-trigger">{children}</span>
      ) : (
        children
      );
    return (
      <Tooltip content={panel} position="top">
        {trigger}
      </Tooltip>
    );
  }
  return (
    <Tooltip content={panel} position="top">
      <IconQuestionCircle
        role="img"
        aria-label="查看说明"
        className={className ? `bcc-field-tip-icon ${className}` : 'bcc-field-tip-icon'}
      />
    </Tooltip>
  );
}
